import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/* Every route shared the one title and description baked into index.html, so
   in a results page a dozen Veyra pages looked like a dozen copies of the same
   page -- and the snippet under each never described what the page was for.
   Product.tsx set its own title and reset it on unmount, which worked but only
   covered titles, only on that route, and races when two routes unmount in the
   wrong order.

   These tags are set imperatively rather than through a helmet library because
   the app has no SSR: nothing reads them at build time, so a dependency would
   buy nothing that four lines of DOM work does not. */

const SITE = "https://veyra.services";

const upsertMeta = (
  selector: string,
  attr: "name" | "property",
  key: string,
  content: string
) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export interface SeoProps {
  title: string;
  description: string;
  /** Defaults to the current path. Give an explicit one where query strings
      are part of the identity, so ?game=x pages do not all collapse onto the
      bare route. */
  canonicalPath?: string;
  image?: string;
  /** Thin, duplicated or gated pages are worth excluding rather than letting
      them dilute the ones that should rank. */
  noindex?: boolean;
  /** Page-level structured data. Rendered into its own script tag and torn
      down on navigation, so a product's schema never outlives its route and
      end up describing the next page. index.html carries the site-wide
      SoftwareApplication and Organization blocks; this is for the per-page
      ones those cannot express, like a product's actual price. */
  jsonLd?: Record<string, unknown> | null;
}

/** Marks the script tag this hook owns, so it is the only one it removes. */
const LD_ATTR = "data-seo-page";

/* A hook rather than a component: these tags belong to the page, not to a
   position in its tree, and a component would have to be threaded into each
   page's JSX -- often needing a fragment wrapper just to sit beside the root
   element. One call at the top of the component is harder to get wrong. */
export function useSeo({
  title,
  description,
  canonicalPath,
  image = `${SITE}/og-card.png`,
  noindex = false,
  jsonLd = null,
}: SeoProps) {
  const location = useLocation();

  /* Serialised here so the effect below depends on a string rather than an
     object literal, which would be a new reference on every render and
     rewrite the tag in a loop. */
  const ldText = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    const existing = document.head.querySelector(`script[${LD_ATTR}]`);
    if (existing) existing.remove();
    if (!ldText) return;
    const el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute(LD_ATTR, "");
    el.textContent = ldText;
    document.head.appendChild(el);
    return () => el.remove();
  }, [ldText]);

  useEffect(() => {
    const path = canonicalPath ?? `${location.pathname}${location.search}`;
    /* Keep the trailing slash on the root so the canonical matches the
       sitemap entry for the same page exactly. */
    const url = `${SITE}${path === "/" ? "/" : path}`;

    document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      description
    );
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:image"]', "property", "og:image", image);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      description
    );
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    /* Removed rather than set to "index" when false: leaving a stale noindex
       behind after navigating away from a gated page would quietly drop a
       public page out of the index. */
    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      upsertMeta('meta[name="robots"]', "name", "robots", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, canonicalPath, image, noindex, location]);
}
